/* eslint-disable @typescript-eslint/no-unnecessary-type-constraint */
import { IconProps } from '@euler/app/components/icons/types';
import { Typography } from '@euler/app/components/typography';
import { HStack } from '@euler/components/layout/Stack';
import { PredefinedHitSlops } from '@euler/functions/predefinedHitSlops';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { Option } from '@euler/model';
import { memo, useCallback } from 'react';
import {
  StyleProp,
  TextStyle,
  TouchableOpacity,
  ViewStyle,
} from 'react-native';
import { Path, Svg } from 'react-native-svg';

const kIconSize = 18;

const InlineRadios = <T extends unknown = any>({
  value,
  options,
  disabled,
  onChange,
  style,
}: {
  options: Option<T>[];
  value?: T;
  disabled?: boolean;
  onChange?: (value: T) => void;
  style?: StyleProp<ViewStyle>;
}) => {
  const handlePress = useCallback(
    (v: T) => {
      if (v === value) return;
      onChange?.(v);
    },
    [onChange, value],
  );
  return (
    <HStack style={style} justifyContent="flex-end">
      {options.map(option => (
        <Item
          key={String(option.value)}
          value={option.value}
          text={option.text}
          disabled={disabled}
          checked={value === option.value}
          onPress={handlePress as any}
        />
      ))}
    </HStack>
  );
};

export default memo(InlineRadios) as typeof InlineRadios;

const Item = memo(
  <T extends unknown = any>({
    value,
    text,
    checked,
    disabled,
    style,
    iconStyle,
    labelStyle,
    onPress,
  }: {
    value: T;
    text: string;
    checked?: boolean;
    disabled?: boolean;
    onPress?: (value: T) => void;
    style?: StyleProp<ViewStyle>;
    iconStyle?: StyleProp<ViewStyle>;
    labelStyle?: StyleProp<TextStyle>;
  }) => {
    const { rpx } = useResponsiveContext();
    const handlePress = useCallback(() => {
      onPress?.(value);
    }, [onPress, value]);
    return (
      <TouchableOpacity
        css={`
          flex-direction: row;
          justify-content: flex-start;
          align-items: center;
          margin-left: ${rpx(10)}px;
        `}
        style={style}
        hitSlop={PredefinedHitSlops.vLarge}
        onPress={handlePress}
        disabled={disabled}
      >
        {checked ? (
          <CheckedIcon style={iconStyle} />
        ) : (
          <UncheckedIcon style={iconStyle} />
        )}
        <Typography.Label
          regular
          size={rpx(14)}
          lineHeight={rpx(24)}
          color={'#333'}
          css={`
            margin-left: ${rpx(3)}px;
          `}
          style={labelStyle}
        >
          {text}
        </Typography.Label>
      </TouchableOpacity>
    );
  },
);

const CheckedIcon = memo(({ size, color = '#207FE7' }: IconProps) => {
  const { rpx } = useResponsiveContext();
  size = size ?? rpx(kIconSize);
  return (
    <Svg viewBox="0 0 1024 1024" width={size} height={size} fill="none">
      <Path
        d="M512.572028 64.021106c-247.112074 0-447.435519 200.324468-447.435519 447.435519 0 247.112074 200.323445 447.435519 447.435519 447.435519 247.111051 0 447.435519-200.323445 447.435519-447.435519C960.00857 264.345574 759.683079 64.021106 512.572028 64.021106zM801.432948 389.292284 454.691783 736.010937c-8.739035 8.739035-20.186761 13.108552-31.635511 13.108552-11.447726 0-22.896476-4.369517-31.635511-13.108552L212.469066 557.080731c-17.47807-17.45658-17.47807-45.791929 0-63.269999 17.47807-17.47807 45.791929-17.47807 63.269999 0l147.317207 147.295718 315.105654-315.084165c17.47807-17.47807 45.791929-17.47807 63.269999 0C818.909995 343.500355 818.909995 371.814214 801.432948 389.292284z"
        fill={color}
      />
    </Svg>
  );
});

const UncheckedIcon = memo(({ size, color = '#777' }: IconProps) => {
  const { rpx } = useResponsiveContext();
  size = size ?? rpx(kIconSize);
  return (
    <Svg viewBox="0 0 1024 1024" width={size} height={size} fill="none">
      <Path
        d="M511.706311 64.021106c-247.27171 0-447.724091 200.453405-447.724091 447.724091 0 247.27171 200.452381 447.725114 447.724091 447.725114 247.270686 0 447.724091-200.453405 447.724091-447.725114C959.430402 264.47451 758.975974 64.021106 511.706311 64.021106zM511.706311 892.310725c-209.844286 0-380.565529-170.721243-380.565529-380.565529s170.721243-380.565529 380.565529-380.565529 380.565529 170.721243 380.565529 380.565529S721.550597 892.310725 511.706311 892.310725z"
        fill={color}
      />
    </Svg>
  );
});
